import React, { useState, useEffect } from "react";
import { db, doc, updateDoc } from "../../../firebaseConfig";
import { DataGrid } from "@mui/x-data-grid";
import { IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { v4 as uuidv4 } from "uuid";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useAtom } from "jotai";
import { themeAtom, isAdminAtom } from "../../../-Atoms";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import { motion } from "framer-motion";

const HMUContainer = ({ jobData, setHasUnsavedChanges }) => {
  const [theme] = useAtom(themeAtom);
  const [isAdmin] = useAtom(isAdminAtom);
  const [hmus, setHmus] = useState([]);

  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [openPicker, setOpenPicker] = useState(null);
  const [isFocused, setIsFocused] = useState(false);

  const autosizeOptions = {
    columns: ["name", "agency"],
  };
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    },
    hover: { scale: 1.02 },
    tap: { scale: 0.98 },
  };

  useEffect(() => {
    if (jobData && jobData.hmu) {
      setHmus(
        Array.isArray(jobData?.hmu)
          ? jobData.hmu.map((hmu) => ({
              ...hmu,
              id: hmu.id || generateUniqueId(hmu),
            }))
          : []
      );
    }
  }, [jobData]);
  
  // Handle focus state for unsaved changes tracking
  useEffect(() => {
    if (isFocused && setHasUnsavedChanges) {
      setHasUnsavedChanges(true);
    }
  }, [isFocused, setHasUnsavedChanges]);

  const generateUniqueId = (hmu) => {
    return hmu.name + hmu.agency + hmu.callTime + hmu.finishTime;
  };

  const updateDatabase = async (updatedHmus) => {
    try {
await updateDoc(doc(db, "JobData", jobData.id), {
        hmu: updatedHmus,
      });
      console.log("Database updated with new hmu.");
    } catch (error) {
      console.error("Error updating database:", error);
    }
  };

  const handleAddHmu = () => {
    if (!isAdmin) return;
    const newHmu = {
      id: uuidv4(),
      name: "",
      agency: "",
      callTime: null,
      finishTime: null,
      insta: "", // Add the insta field here
    };
    const updatedHmus = [...hmus, newHmu];
    setHmus(updatedHmus);
    updateDatabase(updatedHmus);
  };

  const handleDeleteHmus = () => {
    if (!isAdmin || rowSelectionModel.length === 0) return;
    const updatedHmus = hmus.filter(
      (hmu) => !rowSelectionModel.includes(hmu.id)
    );
    setHmus(updatedHmus);
    updateDatabase(updatedHmus);
    setRowSelectionModel([]);
  };

  const handleTimeChange = (newValue, rowId, timeType) => {
    const newTime = newValue?.toISOString();
    setHmus((prevHmus) => {
      const updatedHmus = prevHmus.map((hmu) =>
        hmu.id === rowId ? { ...hmu, [timeType]: newTime } : hmu
      );
      updateDatabase(updatedHmus);
      return updatedHmus;
    });
    setOpenPicker(null); // Close the currently open picker after time change
    if (setHasUnsavedChanges) {
      setHasUnsavedChanges(false);
    }
  };

  const handleTimePickerOpen = (rowId, timeType, defaultTime) => {
    setOpenPicker(timeType); // Set the open picker state
    setIsFocused(true);
    setHmus((prevHmus) => {
      const currentHmu = prevHmus.find((hmu) => hmu.id === rowId);
      if (!currentHmu[timeType]) {
        const updatedHmus = prevHmus.map((hmu) =>
          hmu.id === rowId
            ? { ...hmu, [timeType]: defaultTime.toISOString() }
            : hmu
        );
        updateDatabase(updatedHmus);
        return updatedHmus;
      }
      return prevHmus;
    });
  };

  const columns = [
    { field: "name", headerName: "Name", minWidth: 120, editable: isAdmin },
    { field: "agency", headerName: "Agency", width: 115, editable: isAdmin },
    {
      field: "callTime",
      headerName: "Call Time",
      width: 148,
      renderCell: (params) => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            value={params.row.callTime ? dayjs(params.row.callTime) : null}
            disabled={openPicker === "finishTime"} // Disable when the finishTime picker is open
            onOpen={() =>
              handleTimePickerOpen(
                params.row.id,
                "callTime",
                dayjs().hour(8).minute(30)
              )
            }
            onClose={() => {
              setOpenPicker(null);
              setIsFocused(false);
              if (setHasUnsavedChanges) {
                setHasUnsavedChanges(false);
              }
            }}
            onChange={(newValue) =>
              handleTimeChange(newValue, params.row.id, "callTime")
            }
            onAccept={(newValue) =>
              handleTimeChange(newValue, params.row.id, "callTime")
            } // Triggered when OK is pressed
            slotProps={{
              textField: {
                size: "small",
                variant: "filled",
                InputProps: { disableUnderline: true },
                sx: {
                  backgroundColor: 
                    theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
                  boxShadow:
                    theme === "dark"
                      ? "0 2px 5px rgba(0,0,0,0.2)"
                      : "0 2px 5px rgba(0,0,0,0.05)",
                  borderRadius: "8px"
                }
              },
            }}
          />
        </LocalizationProvider>
      ),
      editable: isAdmin,
    },
    {
      field: "finishTime",
      headerName: "Finish Time",
      width: 148,
      renderCell: (params) => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            value={params.row.finishTime ? dayjs(params.row.finishTime) : null}
            disabled={openPicker === "callTime"} // Disable when the callTime picker is open
            onOpen={() =>
              handleTimePickerOpen(
                params.row.id,
                "finishTime",
                dayjs().hour(16).minute(30)
              )
            }
            onClose={() => {
              setOpenPicker(null);
              setIsFocused(false);
              if (setHasUnsavedChanges) {
                setHasUnsavedChanges(false);
              }
            }}
            onChange={(newValue) =>
              handleTimeChange(newValue, params.row.id, "finishTime")
            }
            onAccept={(newValue) =>
              handleTimeChange(newValue, params.row.id, "finishTime")
            } // Triggered when OK is pressed
            slotProps={{
              textField: {
                size: "small",
                variant: "filled",
                InputProps: { disableUnderline: true },
                sx: {
                  backgroundColor: 
                    theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
                  boxShadow:
                    theme === "dark"
                      ? "0 2px 5px rgba(0,0,0,0.2)"
                      : "0 2px 5px rgba(0,0,0,0.05)",
                  borderRadius: "8px"
                }
              },
            }}
          />
        </LocalizationProvider>
      ),
      editable: isAdmin,
    },
    { field: "insta", headerName: "Insta", flex: 1, editable: true },
  ];

  return (
    <FormControl
      fullWidth
      sx={{
        marginTop: 2,
        position: "relative",
      }}
      variant="filled"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          borderRadius: "10px",
          overflow: "hidden",
          backgroundColor:
            theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow:
            theme === "dark"
              ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
              : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
          padding: 1.5,
        }}
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color:
                  theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
                fontWeight: "bold",
                fontSize: "1.1rem",
              }}
            >
              Hair & Makeup Artists
            </Typography>
            <Box sx={{ display: isAdmin ? "flex" : "none" }}>
              <motion.div
                variants={itemVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <IconButton 
                  onClick={handleAddHmu}
                  sx={{
                    backgroundColor: theme === "dark" ? "rgba(45,45,45,0.7)" : "rgba(245,245,245,0.9)",
                    '&:hover': {
                      backgroundColor: theme === "dark" ? "rgba(55,55,55,0.9)" : "rgba(235,235,235,1)",
                    }
                  }}
                >
                  <AddIcon />
                </IconButton>
              </motion.div>
              <motion.div
                variants={itemVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <IconButton
                  onClick={handleDeleteHmus}
                  disabled={rowSelectionModel.length === 0}
                  sx={{
                    ml: 1,
                    backgroundColor: theme === "dark" ? "rgba(45,45,45,0.7)" : "rgba(245,245,245,0.9)",
                    '&:hover': {
                      backgroundColor: theme === "dark" ? "rgba(55,55,55,0.9)" : "rgba(235,235,235,1)",
                    }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </motion.div>
            </Box>
          </Box>
          <Box
            sx={{
              '& .MuiDataGrid-root': {
                border: 'none',
                backgroundColor: theme === "dark" ? "rgba(45,45,45,0.5)" : "rgba(250,250,250,0.5)",
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: theme === "dark" ? "0 2px 5px rgba(0,0,0,0.2)" : "0 2px 5px rgba(0,0,0,0.05)",
              },
              '& .MuiDataGrid-cell': {
                borderBottom: theme === "dark" ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: theme === "dark" ? "rgba(40,40,40,0.9)" : "rgba(240,240,240,0.9)",
                borderBottom: 'none',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 'bold',
              },
            }}
          >
            <DataGrid
              rows={hmus}
              columns={columns}
              checkboxSelection={isAdmin}
              disableRowSelectionOnClick
              autosizeOnMount
              autosizeOptions={autosizeOptions}
              rowSelectionModel={rowSelectionModel}
              disableColumnSorting
              onRowSelectionModelChange={(newSelection) => {
                setRowSelectionModel(newSelection);
              }}
              processRowUpdate={(updatedRow) => {
                setIsFocused(true);
                const updatedHmus = hmus.map((hmu) =>
                  hmu.id === updatedRow.id ? updatedRow : hmu
                );
                setHmus(updatedHmus);
                updateDatabase(updatedHmus);
                if (setHasUnsavedChanges) {
                  setHasUnsavedChanges(false);
                }
                return updatedRow;
              }}
              onProcessRowUpdateError={(error) => console.error(error)}
              getRowId={(row) => row.id || generateUniqueId(row)} // Ensure every row has a unique ID
              autoHeight
              sx={{
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-cell:focus-within': {
                  outline: 'none',
                },
              }}
              onCellEditStart={() => {
                setIsFocused(true);
                if (setHasUnsavedChanges) {
                  setHasUnsavedChanges(true);
                }
              }}
              onCellEditStop={() => {
                setIsFocused(false);
                if (setHasUnsavedChanges) {
                  setHasUnsavedChanges(false);
                }
              }}
            />
          </Box>
        </motion.div>
      </Box>
    </FormControl>
  );
};

export default HMUContainer;
